/**
 * Page 2 of Create trigger drawer
 */

import {TargetBlank} from '@wandb/weave/common/util/links';
import {assertUnreachable} from '@wandb/weave/common/util/types';
import {Button} from '@wandb/weave/components/Button';
import {Select} from '@wandb/weave/components/Form/Select';
import {Icon, IconName} from '@wandb/weave/components/Icon';
import React, {useMemo} from 'react';
import {FormatOptionLabelMeta} from 'react-select';
import {toast} from 'react-toastify';
import {useShallow} from 'zustand/react/shallow';

import {
  EventTriggeringConditionType,
  useEntityIntegrationsQuery,
} from '../../../../generated/graphql';
import {Analytics} from '../../../../services/analytics';
import DocUrls from '../../../../util/doc_urls';
import {InstrumentedLoader} from '../../../utility/InstrumentedLoader';
import {useWebhooksFromEntity} from '../../../Webhooks/hooks';
import {SelectEntity} from '../../Queues/SelectEntity';
import {useEntityHasJobs} from '../hooks';
import {TriggeredActionType} from '../TriggersTable/utilTypes';
import {useActionConfigStore} from './ActionStore';
import * as S from './CreateTriggerDrawerLoaded.styles';
import {EmptyTriggerDrawer} from './EmptyTriggerDrawer';
import {useEventStore} from './EventStore';
import {JobConfiguration} from './JobConfiguration';
import {NotificationSection, useSlackIntegrations} from './NotificationSection';
import {SeparatedChunk} from './shared';
import {SwitchToBasicButton} from './SwitchToBasicButton';
import {WebhookConfiguration} from './WebhookConfiguration';

export type TriggeredActionOptionType = {
  label: string;
  value: TriggeredActionType;
  numActions?: number;
};

type Props = {
  onClose: () => void;
};

export const DrawerPageActionConfiguration: React.FC<Props> = ({onClose}) => {
  const actionConfig = useActionConfigStore(useShallow(s => s));
  const eventType = useEventStore(s => s.eventType);
  const isUpdating = useEventStore(s => s.initProps.isUpdating);
  const {
    webhooks,
    loading: webhooksLoading,
    error: webhooksError,
  } = useWebhooks(actionConfig.entityName);
  const {
    slackIntegrations,
    loading: slackIntegrationsLoading,
    error: slackIntegrationsError,
  } = useSlackIntegrations(actionConfig.entityName);

  const {
    data: entityHasJobArtifacts,
    loading: jobsLoading,
    error: jobsError,
  } = useEntityHasJobs(actionConfig.entityName);
  const triggeredActionTypeOptions: TriggeredActionOptionType[] =
    useMemo(() => {
      const options: TriggeredActionOptionType[] = [];
      if (
        eventType !== EventTriggeringConditionType.RunMetric &&
        // don't currently support editing launch jobs
        !isUpdating
      ) {
        // with deprecation of launch, no current plans to support
        // it for run metric changes
        options.push({
          label: 'Jobs',
          value: 'QueueJobTriggeredAction',
        });
      }
      options.push(
        {
          label: 'Webhooks',
          value: 'GenericWebhookTriggeredAction',
          numActions: webhooks.length,
        },
        {
          label: 'Slack notification',
          value: 'NotificationTriggeredAction',
          numActions: slackIntegrations.length,
        }
      );

      return options;
    }, [eventType, isUpdating, webhooks.length, slackIntegrations.length]);

  let actionTypeOption: TriggeredActionOptionType | null = null;
  if (actionConfig.actionType === 'QueueJobTriggeredAction') {
    actionTypeOption = {
      label: 'Jobs',
      value: 'QueueJobTriggeredAction',
    };
  } else if (actionConfig.actionType === 'GenericWebhookTriggeredAction') {
    actionTypeOption = {
      label: 'Webhooks',
      value: 'GenericWebhookTriggeredAction',
    };
  } else if (actionConfig.actionType === 'NotificationTriggeredAction') {
    actionTypeOption = {
      label: 'Slack notification',
      value: 'NotificationTriggeredAction',
    };
  } else if (actionConfig.actionType === undefined) {
    actionTypeOption = null;
  } else {
    assertUnreachable(actionConfig.actionType);
  }

  const noJobs = !entityHasJobArtifacts;
  const noWebhooks = webhooks.length === 0;
  const hasActions = !noJobs || !noWebhooks;
  const showNoJobsError =
    noJobs &&
    actionConfig.actionType &&
    actionConfig.actionType === 'QueueJobTriggeredAction';
  const showNoWebhooksError =
    noWebhooks &&
    actionConfig.actionType &&
    actionConfig.actionType === 'GenericWebhookTriggeredAction';

  React.useEffect(() => {
    Analytics.track('Automation Creation', {
      type: 'page_view',
      page: 'action_configuration',
      eventType,
    });
    // Only track on initial render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (jobsError) {
    onClose();
    toast.error('Error fetching jobs information');
    return null;
  }
  if (webhooksError) {
    onClose();
    toast.error('Error fetching webhooks information');
    return null;
  }

  if (webhooksLoading || jobsLoading) {
    return <InstrumentedLoader active name="create-trigger-drawer-projects" />;
  }
  const showHelpFooter = hasActions || actionConfig.entityName === '';

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflow: 'auto',
      }}>
      <div style={{flex: 1, padding: '2px'}}>
        <S.PageLabelRow>Step 2 of 3 - Action configuration</S.PageLabelRow>

        <S.LabelRow>
          <S.Label>Team</S.Label>
        </S.LabelRow>
        <SelectEntity
          includeAll={false}
          value={actionConfig.entityName ?? ''}
          onSelectEntity={en => {
            actionConfig.setEntityName(en);
          }}
          includePersonalEntity={false}
        />
        {actionConfig.entityName != null && (
          <>
            {noJobs &&
            noWebhooks &&
            !actionConfig.metricChangeNotificationsEnabled ? (
              /* TODO: remove empty trigger drawer when the flag is removed - no longer makes sense. */
              <EmptyTriggerDrawer />
            ) : (
              <>
                <S.LabelRow>
                  <S.Label>Action type</S.Label>
                </S.LabelRow>
                <Select<TriggeredActionOptionType | null>
                  options={triggeredActionTypeOptions}
                  placeholder="Select an action type..."
                  value={actionTypeOption}
                  onChange={(d: TriggeredActionOptionType | null) => {
                    if (d) {
                      actionConfig.setActionType(d.value);
                      Analytics.track('Automation Creation', {
                        type: 'action_selected',
                        actionType: d.value,
                        eventType,
                        entityName: actionConfig.entityName,
                      });
                    }
                  }}
                  formatOptionLabel={formatActionTypeOptionLabel}
                  errorState={showNoJobsError || showNoWebhooksError}
                />
                {actionConfig.actionType === 'GenericWebhookTriggeredAction' &&
                  (noWebhooks ? (
                    <S.ErrorText>No webhooks available</S.ErrorText>
                  ) : (
                    <WebhookConfiguration
                      onClose={onClose}
                      webhooks={webhooks}
                    />
                  ))}
                {actionConfig.actionType === 'QueueJobTriggeredAction' &&
                  (noJobs ? (
                    <S.ErrorText>No jobs available</S.ErrorText>
                  ) : (
                    <JobConfiguration onClose={onClose} />
                  ))}
                {actionConfig.actionType === 'NotificationTriggeredAction' && (
                  <NotificationSection
                    integrations={slackIntegrations}
                    slackIntegrationsLoading={slackIntegrationsLoading}
                    slackIntegrationsError={slackIntegrationsError}
                  />
                )}
                <SeparatedChunk>
                  <SwitchToBasicButton />
                </SeparatedChunk>
              </>
            )}
          </>
        )}
      </div>
      {showHelpFooter && (
        <div style={{marginTop: 'auto'}}>
          <S.Help>
            <S.HelpMessage>
              Need help? Check out our documentation:
            </S.HelpMessage>
            <TargetBlank href={DocUrls.automation}>
              <Button size="large" variant="secondary" icon="documentation">
                Documentation
              </Button>
            </TargetBlank>
          </S.Help>
        </div>
      )}
    </div>
  );
};

const useWebhooks = (entityName: string | undefined) => {
  const entityIntegrations = useEntityIntegrationsQuery({
    variables: {
      name: entityName ?? '' /* ?'' valid because of skip below */,
    },
    skip: entityName == null,
  });
  const webhooks = useWebhooksFromEntity(entityIntegrations?.data?.entity);
  return {
    webhooks,
    loading: entityIntegrations.loading,
    error: entityIntegrations.error,
  };
};

const formatActionTypeOptionLabel = (
  actionOption: TriggeredActionOptionType | null,
  context: FormatOptionLabelMeta<TriggeredActionOptionType | null>
) => {
  if (actionOption == null) {
    return;
  }

  const {value, label} = actionOption;
  let iconName: IconName;
  switch (value) {
    case 'GenericWebhookTriggeredAction':
      iconName = 'webhook';
      break;
    case 'QueueJobTriggeredAction':
      iconName = 'flash-bolt';
      break;
    case 'NotificationTriggeredAction':
      iconName = 'bell-notifications';
      break;
    default:
      assertUnreachable(value);
  }
  const selected = context.selectValue[0];
  const isSelected = selected === actionOption;
  if (isSelected) {
    return (
      <S.SelectOption>
        <Icon name={iconName} style={{marginBottom: 'auto'}} />
        <S.SelectOptionLabel>
          <S.SelectOptionLabelText>{label}</S.SelectOptionLabelText>
        </S.SelectOptionLabel>
      </S.SelectOption>
    );
  }

  return (
    <S.SelectOption>
      <Icon name={iconName} style={{marginBottom: 'auto'}} />
      <S.SelectOptionLabel>
        <S.SelectOptionLabelText>{label}</S.SelectOptionLabelText>
      </S.SelectOptionLabel>
    </S.SelectOption>
  );
};
